import { Avatar, IconButton, Link, Stack, Typography } from "@mui/material";
import MailIcon from "@mui/icons-material/Mail";
import { Link as RouterLink } from "react-router-dom";
import { telemetryProps } from "../../../instrumentation/gtm";
import {
  LinkedItProject,
  ProjectIndividual,
  Site,
  SiteContext,
} from "../canonical";

interface ProjectDetailsDashContactsProps {
  selectedProject: ProjectIndividual | undefined;
  /** Which site is rendering - controls display of account info */
  site?: SiteContext;
  contractName?: string | null;
  onContractClick?: () => void;
  linkedItProjects?: LinkedItProject[];
  buildItProjectHref?: (project: LinkedItProject) => string;
}

/**
 * Parses an email in format "FirstName LastName <email@domain.com>" or "email@domain.com"
 * and extracts the display name or returns the email if no name is found
 */
function parseEmailForName(email: string | null | undefined): {
  firstName: string;
  lastName: string;
  displayName: string;
} | null {
  if (!email) return null;

  // Try to parse "FirstName LastName <email>" format
  const match = email.match(/^(.+?)\s*<.+>$/);
  if (match) {
    const namePart = match[1].trim();
    const parts = namePart.split(" ");
    const firstName = parts[0] || "";
    const lastName = parts.slice(1).join(" ") || "";
    return {
      firstName,
      lastName,
      displayName: namePart,
    };
  }

  // Just an email address - use part before @ as display
  const atIndex = email.indexOf("@");
  if (atIndex > 0) {
    const localPart = email.substring(0, atIndex);
    // Try to split by . to get first.last
    const parts = localPart.split(".");
    if (parts.length >= 2) {
      return {
        firstName: parts[0],
        lastName: parts.slice(1).join(" "),
        displayName: parts
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
          .join(" "),
      };
    }
    return {
      firstName: localPart,
      lastName: "",
      displayName: localPart,
    };
  }

  return null;
}

function getInitials(name: string): string {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

interface ContactDisplayProps {
  label: string;
  fullName: string;
  email: string;
}

/**
 * Extracts just the email address from a string that might be in format "Name <email>" or just "email"
 */
function extractEmailAddress(email: string): string {
  const match = email.match(/<(.+)>/);
  return match ? match[1] : email;
}

const ContactDisplay: React.FC<ContactDisplayProps> = ({
  label,
  fullName,
  email,
}) => {
  const mailtoEmail = extractEmailAddress(email);
  return (
    <Stack sx={{ flex: 1 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, mb: 0.5 }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1.2}>
        <Avatar sx={{ width: 40, height: 40 }}>{getInitials(fullName)}</Avatar>
        <Typography variant="body2">{fullName}</Typography>
        {mailtoEmail && (
          <IconButton
            aria-label="Mail"
            href={`mailto:${mailtoEmail}`}
            size="small"
          >
            <MailIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

const ProjectDetailsDashContacts: React.FC<ProjectDetailsDashContactsProps> = ({
  selectedProject,
  site = Site.CustomerExperience,
  contractName,
  onContractClick,
  linkedItProjects,
  buildItProjectHref,
}) => {
  const project = selectedProject?.project;

  // Customer Success shows account info since users can view any account's projects
  const showAccountInfo = site === Site.CustomerSuccess;

  const accountExecutive = parseEmailForName(project?.accountExecutive);
  const projLeadName = project?.projectLeadLastName
    ? `${project?.projectLeadFirstName ?? ""} ${project?.projectLeadLastName}`.trim()
    : null;

  return (
    <Stack>
      {/* Account info - only shown in Customer Success */}
      {showAccountInfo && project?.accountName && (
        <Stack sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Account
          </Typography>
          <Typography variant="body1">
            {project.accountName}
            {project.axAccountNumber && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                (AX# {project.axAccountNumber})
              </Typography>
            )}
          </Typography>
        </Stack>
      )}

      {contractName && (
        <Stack sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Associated Contract
          </Typography>
          {onContractClick ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              color="primary"
              onClick={onContractClick}
              {...telemetryProps({
                name: "project_details_contract_open",
                section: "project_details",
                label: "Associated Contract",
              })}
              sx={{
                alignSelf: "flex-start",
                textAlign: "left",
                typography: "body1",
              }}
            >
              {contractName}
            </Link>
          ) : (
            <Typography variant="body1">{contractName}</Typography>
          )}
        </Stack>
      )}

      {linkedItProjects?.length && buildItProjectHref ? (
        <Stack sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {linkedItProjects.length === 1 ? "Project" : "Projects"}
          </Typography>
          <Stack alignItems="flex-start" sx={{ overflowWrap: "anywhere" }}>
            {linkedItProjects.map((project) => (
              <Link
                key={project.itProjectId}
                component={RouterLink}
                to={buildItProjectHref(project)}
                underline="hover"
                color="primary"
                sx={{
                  typography: "body1",
                  fontWeight: 400,
                }}
              >
                {project.title}
              </Link>
            ))}
          </Stack>
        </Stack>
      ) : null}
      <Stack direction={{ xs: "column", xl: "row" }} sx={{ gap: 2 }}>
        {projLeadName && (
          <ContactDisplay
            label="Engagement Lead"
            fullName={projLeadName}
            email={project?.projectLeadEmail ?? ""}
          />
        )}
        {accountExecutive && (
          <ContactDisplay
            label="Account Executive"
            fullName={accountExecutive.displayName}
            email={project?.accountExecutive ?? ""}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ProjectDetailsDashContacts;
